import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import { getNewsById, getRelatedNews, newsList } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function NewsDetailPage() {
  const { id } = useParams();
  const article = getNewsById(id);
  const related = article ? getRelatedNews(article, 3) : [];
  const latest = newsList.filter((item) => item.id !== id).slice(0, 4);

  usePageMeta({
    title: article
      ? `${article.title} - 影视资讯 - “奸情”影视内容站`
      : "资讯不存在 - “奸情”影视内容站",
    description: article
      ? `${article.summary.slice(0, 80)}…… 发布于 ${article.date}，分类：${article.category}。`
      : "你访问的资讯不存在或已下线，可以返回资讯栏目继续浏览。",
    keywords: article ? `${article.category},影视资讯,${article.title}` : "影视资讯,幕后揭秘",
  });

  if (!article) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 pb-20 pt-32">
          <div className="w-full max-w-lg rounded-lg border border-background-200 bg-background-100 px-6 py-14 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-2xl text-foreground-400">
              <i className="ri-article-line" aria-hidden="true" />
            </span>
            <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">这篇资讯暂时找不到</h1>
            <p className="mt-2 text-sm leading-6 text-foreground-500">
              文章可能已下线，或者链接地址有误。你可以回到资讯栏目继续浏览。
            </p>
            <Link
              to="/news"
              className="mt-6 inline-flex items-center justify-center gap-1.5 rounded-md bg-primary-900 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors whitespace-nowrap hover:bg-primary-800"
            >
              返回资讯栏目
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <article className="w-full px-4 pb-10 pt-24 md:px-8 md:pt-32 lg:px-12">
          <div className="mx-auto w-full max-w-4xl">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "资讯栏目", to: "/news" },
                { label: article.category, to: "/news" },
                { label: article.title },
              ]}
            />

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-accent-100 px-2.5 py-1 text-[11px] font-medium text-accent-800">
                {article.category}
              </span>
              <span className="font-label text-xs text-foreground-400">{article.date}</span>
              <span className="text-xs text-foreground-400">· {article.author}</span>
            </div>

            <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <p className="mt-4 border-l-2 border-accent-400 pl-4 text-sm leading-7 text-foreground-600 md:text-[15px]">
              {article.summary}
            </p>
          </div>

          <figure className="mx-auto mt-8 w-full max-w-4xl overflow-hidden rounded-lg border border-background-200">
            <img
              src={article.cover}
              alt={`${article.title}配图`}
              title={`${article.title} - 影视资讯配图`}
              className="w-full aspect-[16/9] object-cover object-top"
            />
            <figcaption className="bg-background-100 px-4 py-2 text-[11px] text-foreground-500">
              图 · 资讯配图，仅用于页面内容展示
            </figcaption>
          </figure>

          <div className="mx-auto mt-10 w-full max-w-3xl">
            <div className="flex flex-col gap-5">
              {article.content.map((paragraph) => (
                <p key={paragraph.slice(0, 20)} className="text-sm leading-8 text-foreground-800 md:text-base md:leading-9">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-sm font-bold text-foreground-950">继续阅读</h2>
              <p className="mt-2 text-sm leading-6 text-foreground-600">
                想了解更多剧集解析？可以前往影视剧分类，或使用全站搜索按关键词查找你想看的剧。
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to="/news"
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary-900 px-4 py-2 text-xs font-semibold text-background-50 transition-colors whitespace-nowrap hover:bg-primary-800 md:text-sm"
                >
                  返回资讯列表
                </Link>
                <Link
                  to="/category"
                  className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-4 py-2 text-xs font-medium text-foreground-700 transition-colors whitespace-nowrap hover:border-primary-300 hover:text-primary-700 md:text-sm"
                >
                  浏览影视剧分类
                </Link>
              </div>
            </div>
          </div>
        </article>

        <section className="w-full bg-background-100 px-4 py-12 md:px-8 md:py-16 lg:px-12">
          <div className="mx-auto w-full max-w-4xl">
            <div className="flex items-end justify-between">
              <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">相关推荐资讯</h2>
              <Link to="/news" className="text-xs font-medium text-primary-700 transition-colors hover:text-accent-600">
                全部资讯
              </Link>
            </div>

            {related.length > 0 ? (
              <div className="mt-5 flex flex-col gap-3">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/${item.id}`}
                    className="group flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:flex-row md:items-center md:gap-5"
                  >
                    <div className="h-[150px] w-full shrink-0 overflow-hidden rounded-md md:h-[90px] md:w-[150px]">
                      <img
                        src={item.cover}
                        alt={`${item.title}配图`}
                        title={`${item.title} - 相关资讯`}
                        className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 text-[11px]">
                        <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-secondary-900">{item.category}</span>
                        <span className="font-label text-foreground-400">{item.date}</span>
                      </div>
                      <h3 className="mt-1.5 font-heading text-sm font-bold text-foreground-900 transition-colors group-hover:text-primary-700 md:text-base">
                        {item.title}
                      </h3>
                    </div>
                    <i className="ri-arrow-right-line hidden text-lg text-foreground-300 group-hover:text-accent-500 md:block" aria-hidden="true" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="mt-5 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-12 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-xl text-foreground-400">
                  <i className="ri-inbox-line" aria-hidden="true" />
                </span>
                <p className="mt-4 text-sm font-medium text-foreground-700">暂无更多相关内容</p>
                <p className="mt-1.5 text-xs text-foreground-500">这篇资讯暂时没有关联文章，先看看下面这些吧。</p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {latest.map((item) => (
                    <Link
                      key={item.id}
                      to={`/news/${item.id}`}
                      className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}